import {
  AfterViewChecked,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { IWidget } from '../../../interfaces/iWidget';
import { SettingsService } from '../../../services/settings.service';
import { OnlineuserService } from '../../../services/onlineuser.service';
import { SynchronizationService } from '../../../services/synchronization.service';
import { SyncEventType } from '../../../models/enums/syncEventType';
import { EventMessageCreated } from '../../../models/syncEvents/chatEvents/eventMessageCreated';
import { filter, Observable, Subscription, switchMap, take } from 'rxjs';
import { Channel } from '../../../models/channel';
import { ChannelService } from '../../../services/channel.service';
import { SettingsEventType } from '../../../models/enums/settingsEventType';
import { Message } from '../../../models/message';
import { PersonService } from '../../../services/person.service';
import { Person } from '../../../models/person';
import { tap } from 'rxjs/operators';
import { Issue } from '../../../models/issue';
import { IssuecardHelper } from '../../issuecard/issuecard.helper';
import { Userstory } from '../../../models/userstory';
import { Status } from '../../../models/status';
import { Statuscategory } from '../../../models/statuscategory';
import { IssueComponent } from '../../issue/issue.component';
import { UserstoryService } from '../../../services/userstory.service';
import { EventIssueOpened } from '../../../models/syncEvents/persistenceEvents/eventIssueOpened';
import { IssuecardComponent } from '../../issuecard/issuecard.component';
import { IssueSynchroHelper } from '../../issue/issue.synchro.helper';
import { DynamicElementService } from '../../../services/dynamicElement.service';
import { HTMLElementPositioningHelper } from '../../../util/htmlelement-positioning-helper';
import { TranslateService } from '@ngx-translate/core';
import { CopilotService } from '../../../services/copilot.service';
import { MessageService } from '../../../services/message.service';
import { CommActionItem } from '../../../interfaces/comm-action-item';
import { IssueType } from '../../../models/enums/issueType';
import { ChatItemGroup } from '../../../interfaces/chatItemGroup';
import { IssueState } from '../../../models/enums/issueStates';
import { AiState } from '../../../models/enums/aiState';
import { IssueTypeMappingUtil } from '../../../util/issuetype-mapping-util';
import { EventActionItemEdited } from '../../../models/syncEvents/chatEvents/eventActionItemEdited';
import { ActionItemType } from '../../../models/enums/actionItemType';
import { EventActionItemCreated } from '../../../models/syncEvents/chatEvents/eventActionItemCreated';
import { EventAiStateChanged } from '../../../models/syncEvents/chatEvents/EventAiStateChanged';

@Component({
  selector: 'app-chat-widget',
  templateUrl: './chat-widget.component.html',
  styleUrls: ['./chat-widget.component.scss'],
  })
export class ChatWidgetComponent implements IWidget, AfterViewChecked, OnInit, OnDestroy {
  static widgetName = 'chat';
  @ViewChild('chatWidgetContainer', { static: true }) private chatWidgetContainer: ElementRef;
  @ViewChild('scrollMe') private myScrollContainer: ElementRef;
  @ViewChild('contextMenu', { static: true }) private contextMenu: ElementRef;
  @ViewChild('newBklIssue', { read: ViewContainerRef }) newBacklogIssueComponentChild: ViewContainerRef;
  @ViewChild('autoResizeTextarea', { static: false }) autoResizeTextarea: ElementRef;

  @Input() channel: Channel;

  @Output() issuecardClosed = new EventEmitter<Issue>();
  @Output() issueRemoved = new EventEmitter<Issue>();
  @Output() remove = new EventEmitter<number>();

  @HostListener('window:scroll')

  public componentRef: ComponentRef<any>;
  public isUndocked: boolean;
  public projectKey = '';
  public subscriptions: Subscription[] = [];
  public actionItems: CommActionItem[] = [];
  public isActionItemShowing = true;
  private tempIssueId: number = null;
  private scrollThreshold = 15;

  // private members: Observable<Person[]>;
  private mappedMembers: Person[] = [];
  private membersIds: string[] = [];
  private messagesToAnalyze: Message[] = [];
  public currentDate = '';
  public openActionItemsCount = 0;
  public acceptedActionItemsCount = 0;
  public rejectedActionItemsCount = 0;

  public contextMenuVisible = false;
  public isMenuExpanded = true;
  private isNearBottom = true;
  public showScrollButton = true;

  protected myUserId: string;

  newMessage: Message = this.createEmptyMessage();
  groupedChatItems: ChatItemGroup[] = [];
  aiState = AiState.DEFAULT;
  allMessagesAnalyzed = false;

  constructor(
    private settingsService: SettingsService,
    private onlineUserService: OnlineuserService,
    private sync: SynchronizationService,
    private channelService: ChannelService,
    private personService: PersonService,
    private userstoryService: UserstoryService,
    private dynamicElementService: DynamicElementService,
    private positionHelper: HTMLElementPositioningHelper,
    private issueCardHelper: IssuecardHelper,
    private translate: TranslateService,
    private copilotService: CopilotService,
    private messageService: MessageService,
  ) {
  }

  ngOnInit(): void {
    this.currentDate = new Date().toLocaleString().split(',')[0];
    this.projectKey = this.settingsService.getProjectName();
    this.sync.joinChatRoom(this.projectKey);
    this.myUserId = this.onlineUserService.getDisplayName();
    this.loadChannel();
    this.registerMessageSubscriber();
    this.registerActionItemsSubcriber();
    this.registerActionItemsEditSubscriber();
    this.registerActionItemIssueUpdatedSubscriber();
    this.registerProjectChangedSubscription();
    this.registerAiStateChangedSubscriber();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.sync.leaveChatRoom();
  }

  ngAfterViewChecked(): void {
    this.adjustTextareaHeight();
    if (this.isNearBottom) {
      this.scrollToBottom();
    }
  }

  private registerMessageSubscriber(): void {
    const messageSubscription = this.sync.subscribeEvent(SyncEventType.MESSAGE_CREATED)
      .subscribe((data: any) => {
        const newMessage = new Message(
          data.content,
          new Date(data.timestamp),
          data.channel_id,
          data.sender_id,
          data.is_ki_ignored,
          data.is_read,
          data.issue_id,
        );
        const formattedDate = this.formatDate(newMessage.timestamp);

        let group = this.groupedChatItems.find((_group) => _group.date === formattedDate);
        if (!group) {
          group = { date: formattedDate, items: [] };
          this.groupedChatItems.push(group);
        }
        group.items.push(newMessage);
      });
    this.subscriptions.push(messageSubscription);
  }

  private registerActionItemsSubcriber(): void {
    const actionItemsSubscription = this.sync
      .subscribeEvent<CommActionItem>(
      SyncEventType.AI_ISSUE_SUGGESTION_CREATED,
    ).subscribe((data) => {
      const newActionItem: CommActionItem = {
        id: data.id,
        title: data.title,
        description: data.description,
        actionItemType: data.actionItemType,
        issueType: data.issueType,
        timestampTriggered: data.timestampTriggered,
        timestampSuggested: data.timestampSuggested,
        timestampProcessed: data.timestampProcessed ? new Date(data.timestampProcessed) : null,
        state: data.state,
        parentActionItemId: data.parentActionItemId,
        issueId: data.issueId,
      };
      this.groupedChatItems[this.groupedChatItems.length - 1].items.push(newActionItem);
    });
    this.subscriptions.push(actionItemsSubscription);
  }

  private registerActionItemsEditSubscriber(): void {
    const actionItemsSubscription = this.sync
      .subscribeEvent<any>(
      SyncEventType.AI_ISSUE_SUGGESTION_EDITED,
    ).subscribe((data) => {

      this.updateActionItem(data.id, data.update, false);
    });
    this.subscriptions.push(actionItemsSubscription);
  }

  private registerActionItemIssueUpdatedSubscriber(): void {
    this.sync.subscribeEvent<any>(SyncEventType.ACTION_ITEM_ISSUE_UPDATED,
    ).subscribe((data) => {
      this.updateActionItemByIssueId(data.id, {
        title: data.summary,
        description: data.description,
        issueType: data.issueType.toUpperCase(),
      });
    });
  }

  private registerProjectChangedSubscription(): void {
    this.subscriptions.push(
      this.settingsService.getUpdateObservable().pipe(
        filter(event => event === SettingsEventType.PROJECT_CHANGED),
      ).subscribe(() => this.reloadChannel(this.settingsService.getProjectName())),
    );
  }

  private registerAiStateChangedSubscriber(): void {
    const aiStateSubscription = this.sync
      .subscribeEvent<EventAiStateChanged>(SyncEventType.AI_STATE_CHANGED)
      .subscribe((event: any) => {
        this.aiState = event.state;
      });
    this.subscriptions.push(aiStateSubscription);
  }

  private loadChannel(): void {
    this.personService.getMembers(this.projectKey).pipe(
      tap(members => {
        this.mappedMembers = members;
        this.membersIds = this.mappedMembers.map(member => member.id);
      }),
      switchMap(() => this.channelService.getChannelByProjectKey(this.projectKey, this.membersIds)),
      switchMap((channel) => {
        this.channel = channel;
        return this.channelService.getMessagesByChannelId(channel.id, this.myUserId);
      }),
      switchMap((messageResponse) => this.processMessagesAndLoadActionItems(messageResponse)),
    ).subscribe({
      next: (actionItemsResponse) => this.handleActionItemsResponse(actionItemsResponse),
      error: (error) => this.handleLoadError(error),
    });
  }

  private processMessagesAndLoadActionItems(messageResponse: Message[]): Observable<any> {
    const sortedMessages = messageResponse.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    this.setMessages(sortedMessages);
    this.messagesToAnalyze = this.checkMessagesForAnalysis();
    return this.channelService.getActionItemsByChannelId(this.projectKey);
  }

  private handleActionItemsResponse(actionItemsResponse: any): void {
    this.actionItems = actionItemsResponse.actionItems;
    this.adjustActionItemsCounter();
    this.mergeIssueSuggestionsIntoChatList(this.actionItems);
  }

  private handleLoadError(error: any): void {
    console.error('Failed to load messages:', error.message);
  }


  private adjustActionItemsCounter() {
    this.openActionItemsCount = this.actionItems.filter(actionItem => actionItem.state === IssueState.OPEN).length;
    this.acceptedActionItemsCount = this.actionItems.filter(actionItem => actionItem.state === IssueState.ACCEPTED).length;
    this.rejectedActionItemsCount = this.actionItems.filter(actionItem => actionItem.state === IssueState.REJECTED).length;
  }

  /**
   * Merges a list of action items into the `groupedMessages` array, grouping them by date
   * and ensuring chronological order within each group.
   *
   * @param {CommActionItem[]} actionItems - The array of action items to merge into grouped messages.
   */
  private mergeIssueSuggestionsIntoChatList(actionItems: CommActionItem[]): void {
    for (const suggestion of actionItems) {
      const suggestionDate = this.formatDate(suggestion.timestampTriggered);

      let group = this.groupedChatItems.find((_group) => _group.date === suggestionDate);
      if (!group) {
        group = { date: suggestionDate, items: [] };
        this.groupedChatItems.push(group);
        this.groupedChatItems.sort((a: ChatItemGroup, b: ChatItemGroup): number => {
          const dateA = new Date(a.date.split('.').reverse().join('-'));
          const dateB = new Date(b.date.split('.').reverse().join('-'));
          return dateA.getTime() - dateB.getTime();
        });
      }
      const index = group.items.findIndex(
        (msg) => ((msg as Message).timestamp || (msg as CommActionItem).timestampTriggered) >
          suggestion.timestampTriggered,
      );
      if (index === -1) {
        group.items.push(suggestion);
      } else {
        group.items.splice(index, 0, suggestion);
      }
    }
  }

  private reloadChannel(projectKey: string): void {
    this.sync.leaveChatRoom();
    this.projectKey = projectKey;
    this.sync.joinChatRoom(this.projectKey);
    this.loadChannel();
  }

  isMe(msg: Message): boolean {
    return msg.senderId === this.onlineUserService.getDisplayName();
  }

  toggleMenu(): void {
    this.isMenuExpanded = !this.isMenuExpanded;
  }

  get toggleText(): string {
    return this.isActionItemShowing ? 'KI Action Items ausblenden' : 'KI Action Items anzeigen';
  }

  setPosition(x: number, y: number): void {
    this.chatWidgetContainer.nativeElement.style.left = `${x}px`;
    this.chatWidgetContainer.nativeElement.style.top = `${y}px`;
  }

  adjustTextareaHeight(): void {
    if (!this.autoResizeTextarea || !this.autoResizeTextarea.nativeElement) {
      return;
    }
    const textarea = this.autoResizeTextarea.nativeElement;
    textarea.style.height = 'auto';
    textarea.style.height = 'auto';

    const newHeight = textarea.scrollHeight;
    const maxHeight = 200;

    if (newHeight > maxHeight) {
      textarea.style.height = maxHeight + 'px';
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.height = newHeight + 'px';
      textarea.style.overflowY = 'hidden';
    }
  }

  sendMessage(text: string, issueId = null) {
    if (text.trim() === '') {
      return;
    }
    this.saveMessage(text, issueId);
    const sendEvent = this.createSendEvent(text, issueId);
    this.sync.sendChatEvent(sendEvent, false);
    this.resetNewMessage();
  }

  private createSendEvent(text: string, issueId = null): EventMessageCreated {
    return new EventMessageCreated({
      content: text,
      timestamp: new Date(),
      channel_id: this.channel.id,
      sender_id: this.onlineUserService.getDisplayName(),
      is_ki_ignored: this.newMessage.isKIIgnored,
      is_read: true,
      issue_id: issueId,
    });
  }

  private saveMessage(text: string, issueId: string | null = null): void {
    this.translate.get('widgets.chat.created').subscribe((translatedText: string) => {
      const extendedText = issueId ? `${text} ${translatedText}` : text;
      const messageToSave = new Message(
        extendedText,
        new Date(),
        this.channel.id,
        this.myUserId,
        this.newMessage.isKIIgnored,
        true,
        issueId,
      );

      this.channelService.saveMessage(messageToSave, this.membersIds).subscribe((response: any) => {
        if (response.message.isKIIgnored) {
          return;
        }
        this.messagesToAnalyze.push({ ...response.message, timestamp: new Date(response.message.timestamp) });
        if (this.messagesToAnalyze.length > 10) {
          this.messagesToAnalyze.shift();
        }
        this.requestAnalysis();
      });
    });
  }

  private resetNewMessage(): void {
    this.newMessage = this.createEmptyMessage();
  }

  private createEmptyMessage(): Message {
    return new Message('', new Date(), 0, '', false, true);
  }

  onScroll() {
    const position = this.myScrollContainer.nativeElement.scrollTop + this.myScrollContainer.nativeElement.offsetHeight;
    const height = this.myScrollContainer.nativeElement.scrollHeight;
    this.isNearBottom = position >= height - this.scrollThreshold;
    this.showScrollButton = !this.isNearBottom;
  }

  public scrollToBottom(): void {
    if (!this.myScrollContainer) {
      return;
    }
    this.myScrollContainer.nativeElement.scrollTop = this.myScrollContainer.nativeElement.scrollHeight;
  }

  getMemberAvatar(senderId: string): string {
    const member = this.mappedMembers.find(m => m.id === senderId);

    if (member && member.avatarImage) {
      if (member.avatarImage.startsWith('data:image')) {
        return member.avatarImage;
      } else {
        return 'data:image/png;base64,' + member.avatarImage;
      }
    }
    return './assets/img/icons/icon_chat_sender.png';
  }

  protected markAsRead(message: Message): void {
    if (!this.isMe(message) && !message.isRead) {
      message.isRead = true;
      this.channelService.updateIsRead(message.id, this.myUserId).pipe(take(1)).subscribe();
    }
  }

  onRightClick(mouseEvent: MouseEvent, message: Message): void {
    mouseEvent.preventDefault();
    const targetElement = mouseEvent.target as HTMLElement;
    const boundingRect = targetElement.getBoundingClientRect();
    this.contextMenu.nativeElement.style.left = `${boundingRect.left}px`;
    this.contextMenu.nativeElement.style.top = `${boundingRect.top}px`;

    this.messagesToAnalyze.push(message);
    if (this.messagesToAnalyze) {
      this.contextMenuVisible = true;
    }
  }

  toggleAiState(): void {
    this.isActionItemShowing = !this.isActionItemShowing;
    if (this.isActionItemShowing) {
      this.messageService.sendDebug('Enable action items');
    }
  }

  /**
   * Filters and retrieves the last 10 messages from a grouped message structure.
   *
   * This method processes `groupedMessages` by flattening all grouped messages
   * into a single array, filtering out any invalid messages (ensuring they meet
   * the `isMessage` criteria), and finally slicing the last 10 messages.
   *
   * @private
   * @returns {Message[]} An array containing up to the last 10 valid messages.
   */
  private checkMessagesForAnalysis(): Message[] {
    const allMessages = this.groupedChatItems
      .flatMap(group => group.items)
      .filter(item => this.isMessage(item))
      .filter((message: Message) => !message.isKIIgnored) as Message[];

    return allMessages.slice(-10);
  }

  isMessage(item: Message | CommActionItem): item is Message {
    return (item as Message).senderId !== undefined;
  }

  isActionItem(item: Message | CommActionItem): item is CommActionItem {
    return (item as CommActionItem).timestampSuggested !== undefined;
  }

  setKIIgnore(): void {
    this.newMessage.isKIIgnored = !this.newMessage.isKIIgnored;
  }

  // will only be shown for 3 seconds
  private showBanner() {
    this.allMessagesAnalyzed = true;
    setTimeout(() => {
      this.allMessagesAnalyzed = false;
    }, 3000);
  }

  requestAnalysis(event?: MouseEvent): void {
    this.updateAitState(AiState.ANALYZING);
    this.contextMenuVisible = false;

    const sprintId = this.settingsService.getSprintId(this.projectKey);

    this.copilotService.createChatAnalysis(
      this.projectKey,
      sprintId,
      this.messagesToAnalyze,
    ).subscribe({
      next: response => {
        if (response.status === 204) {
          this.messageService.sendTrace('Keine neuen Action Items.');
          setTimeout(() => {
            this.updateAitState(AiState.DEFAULT);
            this.showBanner();
          }, 1000);
          return;
        }

        const chatAnalysis = response.body;
        if (!chatAnalysis || chatAnalysis.action_items.length === 0) {
          this.updateAitState(AiState.DEFAULT);
          this.showBanner();
          return;
        }
        this.updateAitState(AiState.GENERATING);
        this.messageService.sendTrace(`Chat analysis details received: ${JSON.stringify(chatAnalysis)}`);

        setTimeout(() => {
          chatAnalysis.action_items.forEach(actionItemResponse => {
            const actionItem: CommActionItem = this.createCommActionItem(actionItemResponse);
            this.actionItems.push(actionItem);
            this.adjustActionItemsCounter();

            if (this.groupedChatItems && this.groupedChatItems.length > 0) {
              this.groupedChatItems[this.groupedChatItems.length - 1].items.push(actionItem);
            }

            const actionItemCreatedEvent = new EventActionItemCreated(actionItem);
            this.sync.sendPersistenceEventNew(actionItemCreatedEvent, true);
          });
          this.updateAitState(AiState.DEFAULT);
          this.showBanner();
        }, 1200);
      },
      error: (error) => {
        console.error('Error during createChatAnalysis:', error);
        this.updateAitState(AiState.DEFAULT);
        this.showBanner();
      },
    });
  }

  private updateAitState(state: AiState): void {
    console.log('in updateAitState:', state);
    this.aiState = state;
    this.sync.sendChatEvent(new EventAiStateChanged(state), true);
  }

  createIssue(event: MouseEvent, text = ''): void {
    this.contextMenuVisible = false;
    const selectedText = window.getSelection()?.toString();
    this.openNewIssueDialog(event, { title: selectedText });
  }

  private createCommActionItem(actionItemResponse: any): CommActionItem {
    return {
      title: actionItemResponse.title,
      description: actionItemResponse.description,
      actionItemType: actionItemResponse.actionItemType,
      issueType: actionItemResponse.issueType,
      timestampTriggered: actionItemResponse.timestampTriggered,
      timestampSuggested: actionItemResponse.timestampSuggested,
      timestampProcessed: actionItemResponse.timestampProcessed,
      state: actionItemResponse.state,
      parentActionItemId: actionItemResponse.parentActionItemId,
      issueId: actionItemResponse.issueId,
      id: actionItemResponse.id,
    };
  }

  onNewIssueCreated(issue: Issue | Userstory): void {
    const text = `#${issue['key']} - ${issue.summary}`;
    this.sendMessage(text, issue['id']);
  }

  openNewIssueDialog(event: MouseEvent, params: { title: string } | { actionItem: CommActionItem }): void {
    const title = 'title' in params ? params.title : null;
    const actionItem = 'actionItem' in params ? params.actionItem : null;
    // Create new Task
    this.tempIssueId = this.userstoryService.getTemporaryIdForNewIssue();
    const issueComp: IssueComponent = this.createIssueComponent(this.tempIssueId, title, actionItem);
    this.tempIssueId = null;
    // Show the IssueCard modal window
    issueComp.createIssuecardForIssue(event, issueComp.issue, false);
  }

  // TODO: Should be split into two componentes. Mabye an ai-action-item-component and a chat-component.
  /**
   * Create a new issueComponent for task creation inside story
   * @param issueId parent story ID
   * @param title
   * @param actionItem
   */
  createIssueComponent(issueId: number, title?: string, actionItem?: CommActionItem): IssueComponent {
    // Create new Task
    const statusTodo: Status = new Status('', '', 10000, 'To Do', new Statuscategory('blue-gray', 2, 'new'));
    const _type = actionItem?.issueType?.toUpperCase() || '';
    const issueType = IssueType[_type as keyof typeof IssueType] ?? IssueType.USERSTORY;

    // const newIssue: Userstory = new Userstory(null, '', undefined, issueId, null, null, title || '',
    //   1, null, statusTodo, [], [], null, null, null, false, null, null, []);
    const newIssue: Userstory = new Userstory(
      null,
      actionItem?.description || '',
      undefined,
      issueId,
      null,
      null,
      actionItem?.title || title,
      issueType || 1,
      null,
      statusTodo,
      [],
      [],
      null,
      null,
      null,
      false,
      null,
      null,
      null,
      [],
    );

    // Dynamically create a new Issue in the row
    const componentRef: ComponentRef<IssueComponent> = this.newBacklogIssueComponentChild.createComponent(IssueComponent);
    const issue: IssueComponent = componentRef.instance;
    issue.issue = newIssue;
    issue.projectKey = this.settingsService.getProjectName();
    issue.componentRef = componentRef;

    componentRef.instance.issueCreated.subscribe(createdIssue => {
      if (!actionItem) {
        return this.onNewIssueCreated(createdIssue);
      }
      const mappedActionItem = this.mapIssueToActionItem(createdIssue);
      this.updateActionItem(actionItem.id, mappedActionItem);
      if (actionItem.parentActionItemId) {
        this.updateActionItem(actionItem.parentActionItemId, { state: IssueState.REJECTED });
      }
    });
    return issue;
  }

  handleIssueClicked(event: MouseEvent, issueId: string, actionItem: CommActionItem | null = null): void {
    const id = Number(issueId);
    if (!issueId || isNaN(id)) {
      console.warn('Ungültige issueId:', issueId);
      return;
    }
    console.log('in handleIssueClicked:', issueId);
    this.userstoryService.getParentIssue(this.settingsService.getProjectName(), Number(issueId)).subscribe(
      res => {
        if (actionItem) {
          res.summary = actionItem.title;
          res.description = actionItem.description;
          const issueTypeMap = {
            'STORY': IssueType.USERSTORY,
            'TASK': IssueType.TASK,
            'BUG': IssueType.BUG,
          };
          res.issueType = issueTypeMap[actionItem.issueType];
        }
        this.createIssuecardForIssue(event, res, actionItem);
      },
    );
  }

  handleActionItemUpdate(actionItemData: { actionItem: CommActionItem; update: Partial<CommActionItem> }): void {
    this.groupedChatItems = this.groupedChatItems.map(group => ({
      ...group,
      items: group.items.map(msg =>
        msg.id === actionItemData.actionItem.id ? ({ ...msg, ...actionItemData.actionItem }) as CommActionItem : msg,
      ),
    }));
    this.adjustActionItemsCounter();
    this.sync.sendChatEvent(new EventActionItemEdited(actionItemData.actionItem.id, actionItemData.update), true);
  }

  updateActionItem(actionItemId: number | null, update: Partial<CommActionItem>, emitEvent = true) {
    console.log('in update action item', actionItemId);
    this.channelService.updateActionItem(actionItemId, update).subscribe(result => {
      this.groupedChatItems = this.groupedChatItems.map(group => ({
        ...group,
        items: group.items.map(msg =>
          msg.id === result.id ? ({ ...msg, ...result }) as CommActionItem : msg,
        ),
      }));
      this.adjustActionItemsCounter();
      if (!emitEvent) {
        return;
      }
      this.sync.sendChatEvent(new EventActionItemEdited(actionItemId, update), true);
    });
  }

  updateActionItemByIssueId(issueId: number, update: Partial<CommActionItem>, emitEvent = true): void {
    console.log('in update action item by issue Id', update);
    this.channelService.updateActionItemByIssueId(issueId, update).subscribe(result => {
      if (!result) {
        return;
      }
      this.groupedChatItems = this.groupedChatItems.map(group => ({
        ...group,
        items: group.items.map(msg =>
          msg.id === result.id ? ({ ...msg, ...update }) as CommActionItem : msg,
        ),
      }));
      this.adjustActionItemsCounter();
      if (!emitEvent) {
        return;
      }
    });
  }

  mapIssueToActionItem(issue: Issue): CommActionItem {
    return {
      title: issue.summary,
      description: issue.description,
      actionItemType: ActionItemType.ACTION_ITEM_NEW,
      issueType: IssueTypeMappingUtil.mapToJiraIssueTypeName(issue.issueType).toUpperCase(),
      timestampProcessed: new Date(),
      state: IssueState.ACCEPTED,
      issueId: issue.id,
    };
  }

  createIssuecardForIssue(event: MouseEvent, issue: Issue, actionItem: CommActionItem, onCreateAssignToSprint = true): void {
    // Dont open a new card if a card for this issue already exists
    if (this.dynamicElementService.containsIssuecardForIssue(issue.id)) {
      return;
    }
    this.sync.sendPersistenceEventNew(new EventIssueOpened(issue.id, this.onlineUserService.getAvatarUrl()));

    // Update last viewed timestamp when card is opened
    this.userstoryService.setLastViewed(this.projectKey, issue.id).subscribe();
    issue.lastViewedInAwall = new Date().toISOString();

    // Create a new card and open it at the correct position
    const card: IssuecardComponent = this.dynamicallyAddIssueCardComponent(issue, '', actionItem, onCreateAssignToSprint);
    const cardElement = card.getElementRef().nativeElement;
    const pos = this.positionHelper.getPositionForNewCard(event, cardElement);
    this.positionHelper.setAbsolutePosition(cardElement, pos.x, pos.y);
    IssueSynchroHelper.sendIssueCardOpenedEvent(this.sync, card, this.positionHelper, this.onlineUserService);
  }

  deleteIssueFromMessage(issueId: number) {
    this.channelService.updateActionItemId(issueId).subscribe({
      next: (message: Message) => {
        let found = false;
        for (const group of this.groupedChatItems) {
          const index = group.items.findIndex(msg => msg.id === message.id);
          if (index >= 0) {
            (group.items[index] as Message).issueId = null;
            found = true;
            break;
          }
        }
        if (!found) {
          console.warn('Message with ID not found in groupedMessages:', message.id);
        } else {
          this.sendMessage(message.content.replace('angelegt', 'gelöscht'));
        }
      },
      error: (error) => {
        console.error('Failed to delete issue from message:', error);
      },
    });
  }

  public setMessages(response: any) {
    const data = response.reduce((result: any, message: any) => {
      const messageDate = this.formatDate(new Date(message.timestamp));

      if (!result[messageDate]) {
        result[messageDate] = {
          date: messageDate,
          items: [],
        };
      }
      result [messageDate].items.push(message);

      return result;
    }, {});

    this.groupedChatItems = Object.values(data);
  }

  public formatDate(date: Date): string {
    return date.toLocaleString().split(',')[0];
  }

  dynamicallyAddIssueCardComponent(
    issue: Issue,
    avatar = '',
    actionItem: CommActionItem,
    onCreateAssignToSprint = true):
    IssuecardComponent {
    const injector = Injector.create({
      providers: [
        { provide: 'issue', useValue: issue },
        { provide: 'projectKey', useValue: this.projectKey },
        { provide: 'statuses', useValue: [] },
        { provide: 'avatar', useValue: avatar },
        {
          provide: 'onCloseCallback', useValue: (issueCard) => {
            this.issueCardHelper.removeCardFromGlobalReferences(issueCard);
            if (this.componentRef) {
              this.componentRef.destroy();
            }
            if (issueCard) {
              issueCard.componentRef.destroy();
            }
            this.issuecardClosed.emit(issue);
          },
        },
        {
          provide: 'onNewIssueCreatedCallback', useValue: () => {
          },
        },
        {
          provide: 'onNewSubIssueCreatedCallback', useValue: () => {
          },
        },
        {
          provide: 'onIssueRemovedCallback', useValue: (issueId) => {
            if (this.componentRef) {
              this.componentRef.destroy();
            }
            this.deleteIssueFromMessage(issueId);
            this.issueRemoved.emit(issueId);
          },
        },
        {
          provide: 'onSubIssueRemovedCallback', useValue: () => {
          },
        },
        {
          provide: 'onIssueModified', useValue: () => {
            actionItem = { ...actionItem, state: IssueState.ACCEPTED };
            this.updateActionItem(actionItem.id, actionItem);
          },
        },
      ],
    });

    const insertionPoint: ViewContainerRef = this.dynamicElementService.getInsertionPoint();
    const componentRef: ComponentRef<IssuecardComponent> = insertionPoint.createComponent(IssuecardComponent, { injector: injector });
    const card: IssuecardComponent = componentRef.instance;
    card.componentRef = componentRef;
    card.onCreateAssignToSprint = onCreateAssignToSprint;

    this.issueCardHelper.setupIssueCardAsDraggable(card);
    this.issueCardHelper.setupIssueCardAsClickable(card);
    this.dynamicElementService.insert(componentRef);
    return card;
  }

  protected readonly IssueState = IssueState;
  protected readonly console = console;
}
